import { get, each, find, invoke, isNaN, isEmpty, map } from 'lodash';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import ThankYouPage from './components/thankYouPage';
import ScreenOutPage from './components/screenOutPage';
import Loading from './components/loading';
import FetchAdditionalSurveysScreen from './components/fetchAdditionalSurveysScreen';
import LanguageSelectModal from './components/languageSelectModal';
import Homescreen from './components/Homescreen';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_DESCRIPTION,
  QUESTION_RANKING,
  QUESTION_SLIDER,
  QUESTION_RATING,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERICAL_OPEN_ENDED,
  ANSWER_OTHER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_NPS,
  INTERACTIVE_MEDIA_TYPES,
  SURVEY_STATE,
  QUESTION_MATRIX_VAN_WESTENDORP,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_MAXDIFF,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  QUESTION_CONVERSATIONAL,
  CONVERSATIONAL_AI_VALUE_STATUSES,
} from './constants';
import translations from './translations';
import feedbackSurvey from './data/feedbackSurvey';
import Survey from './components/survey';
import { overrideBrandUI, applyLogoChanges } from './ui-functions';
import {
  globeIcon,
  chevronIcon,
  volumeIcon,
  muteIcon,
} from './components/icons';
import {
  applyVisibilityActionOnNodeAnswers,
  getLanguageForKey,
  roundNumberToStringWithDecimals,
  transformValuesToString,
} from './utils';
import checkAndEnterSurvey from './components/survey/checkAndEnterSurvey';
import request from './request';
import { publicAssetsPrefix } from '../../../configuration';
import ReactQuestion from './reactComponents/Question';
import isOpenEndedValid from './reactComponents/QuestionBody/OpenEnded/isValid';
import isDrilldownValid from './reactComponents/QuestionBody/DrilldownQuestion/isValid';
import isOpenEndedNumericOrConstantSumValid from './reactComponents/QuestionBody/Matrix/MatrixOpenEnded/isValid';
import isConversationalAiValid from './reactComponents/QuestionBody/Matrix/Conversational/isValid';
import {
  VAN_WESTENDORP_QUESTION_DECIMALS,
  VAN_WESTENDORP_QUESTION_MAX_PRICE,
  VAN_WESTENDORP_QUESTION_MIN_PRICE,
  VAN_WESTENDORP_QUESTION_SORT_ASCENDING,
  VAN_WESTENDORP_QUESTION_SORT_DESCENDING,
} from './reactComponents/QuestionBody/MatrixVanWestendorp/constants';
import {
  ActionVisibilityValueType,
  carryForwardService,
  isMatrixTypeLikert,
} from './logic';

Pollfish.ui = (function ui() {
  return {
    // hold if scroll is fixed for older versions
    scroll: true,
    notifications: [],

    // get texts like terms etc based on the language of the device
    getFallbackText() {
      // fallback to english translations
      if (isEmpty(Pollfish.helper.json.translations)) {
        return translations;
      }
      return Pollfish.helper.json.translations;
    },

    getText(key, value) {
      // need to have a fallback text for the specific translation
      // in case of Pollfish.ui.texts are empty for some reason
      let text = translations[`mobile.${key}`];
      if (!isEmpty(Pollfish.ui.texts)) {
        text = Pollfish.ui.texts[`mobile.${key}`];
      }

      switch (key) {
        case 'surveyPrompt':
        case 'surveyPromptNoIncentive':
          return text && text.replace('_surveyLOI_', value);
        case 'warnings.max_decimals':
        case 'matrixVanWestendorp.warnings.min':
        case 'matrixVanWestendorp.warnings.max':
        case 'warning.displaySamePage.submitButton':
          return text && text.replace('XXX', value);
        case 'warnings.matrixNumericOpenEndedRange':
          return (
            text && text.replace('XXX', value.min).replace('YYY', value.max)
          );
        case 'warnings.matrixMaxDiff':
          return (
            text && text.replace('XXX', value.best).replace('YYY', value.worst)
          );
        default:
          return text;
      }
    },

    // hide an element
    hide(div) {
      $(div).css({ opacity: 0 });
    },

    // get progress bar status based on the index of the question answered
    getProgressBarStatus() {
      let questionsAnswered = Pollfish.helper.getQuestionIndex();
      const questionsCount = Pollfish.store.totalQuestions;

      const questionWrapper = $('.js-question');
      if (questionWrapper.length > 0) {
        const question = Pollfish.ui.getQuestionDataByQuestionWrapperElement(
          questionWrapper,
        );

        const group = Pollfish.survey.getDisplaySamePageGroupByQuestion(
          question,
        );
        const groupDisplaySamePage = invoke(group, 'isDisplaySamePage', false);

        if (groupDisplaySamePage) {
          questionsAnswered = questionsAnswered + group.questions.length - 1;
        }
      }

      let percent = (questionsAnswered / questionsCount) * 100;
      if (percent > 100) {
        percent = 100;
      }
      return `${percent}%`;
    },

    applyMainContentTemplate(template) {
      Pollfish.ui.unmountReactRoot();
      $('.main-content').html(template);
    },

    unmountReactRoot() {
      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      if (Pollfish.reactRoot) {
        const container = document.querySelector('.main-content');
        Pollfish.reactRoot.render(null, container);
      }
    },

    // apply the corresponding template based on the current screen
    applyTemplate(question) {
      // return the correct template

      Raven.captureBreadcrumb({
        message: 'applyTemplate',
        category: 'applyTemplate',
        data: {
          question,
        },
      });

      // update the state/action respondent is into
      if (question && question.isScreening) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_SCREENING);
      } else if (question) {
        Pollfish.surveyState.setState(SURVEY_STATE.ANSWERING_SURVEY_QUESTION);
      }

      this.renderReactComponent(ReactQuestion, { question });

      setTimeout(() => {
        // handle text alignment
        Pollfish.helper.toggleRTL();

        // handle the progress bar
        $('#progressBar').width(Pollfish.ui.getProgressBarStatus());

        // get the current time the question was rendered
        Pollfish.helper.ttc.start = new Date();

        $('html, body').scrollTop(0);
      }, 0);
    },

    renderReactComponent(Component, data) {
      const container = document.querySelector('.main-content');
      // we need to clear the container because sometimes some jquery thing is already rendered there
      container.innerHTML = '';
      Pollfish.reactRoot = createRoot(container);
      Pollfish.reactRoot.render(
        <ErrorBoundary onError={Pollfish.helper.handleGlobalPromiseError}>
          <Component {...data} />
        </ErrorBoundary>,
      );
    },

    // show prompt for a couple of seconds
    prompt(text, className) {
      Pollfish.ui.notifications.push({ text, className });
      Pollfish.ui.showNextPrompt();
    },

    promptSuccess(text) {
      Pollfish.ui.prompt(text, 'success');
    },

    promptError(text) {
      Pollfish.ui.prompt(text, 'error');
    },

    showNextPrompt() {
      // has already at least one running notification
      if (
        Pollfish.ui.notifications.length === 0 ||
        Pollfish.ui.notificationShowing
      ) {
        return;
      }
      Pollfish.ui.notificationShowing = true;
      const prompt = Pollfish.ui.notifications.pop();
      const promptInfo = $('#promptInfo');
      // makes cypress tests run a little faster not waiting for the next notification to show
      const promptTimeout = Pollfish.helper.isCypressEnv() ? 200 : 2000;

      // show prompt
      promptInfo
        .addClass(`prompt-info--visible ${prompt.className}`)
        .find('.prompt-info__text')
        .text(prompt.text);

      // hide prompt
      setTimeout(() => {
        promptInfo.removeClass('prompt-info--visible');
        // remove the color after a while if it existed
        setTimeout(() => {
          promptInfo.removeClass(prompt.className);
          Pollfish.ui.notificationShowing = false;
          Pollfish.ui.showNextPrompt();
        }, 250);
      }, promptTimeout);
    },

    clearNotificationPrompts() {
      const promptInfo = $('#promptInfo');
      promptInfo.removeClass('prompt-info--visible');
      Pollfish.ui.notificationShowing = false;
      Pollfish.ui.notifications = [];
    },

    showSlowDownWarning(responseTime) {
      if (Pollfish.helper.isPreview || Pollfish.helper.isDebugMode()) {
        return;
      }
      const text = Pollfish.ui.getText('read_questions_carefully');
      if (typeof Pollfish.helper.flag.showSlowDownWarning === 'undefined') {
        Pollfish.helper.flag.showSlowDownWarning = 0;
      }

      Pollfish.helper.flag.showSlowDownWarning += 1;
      if (
        responseTime <= 1000 ||
        Pollfish.helper.flag.showSlowDownWarning < 2
      ) {
        setTimeout(() => Pollfish.ui.prompt(text), 0);
      }
    },

    showConfirmationModal(props) {
      const {
        header = '',
        content = '',
        onConfirm = () => {},
        onCancel = () => {},
        useHtmlContent = false,
        noBtnText = Pollfish.ui.getText('no') || 'No',
        yesBtnText = Pollfish.ui.getText('yes') || 'Yes',
        style = undefined,
        buttonStack = 'horizontal',
      } = props;
      const container = $('#js-leave-survey-modal');
      if (style) {
        const modalContent = container.find('.modal-content');
        modalContent.removeAttr('style').css(style);
      }
      if (buttonStack === 'vertical') {
        container.addClass('vertical-button-stack');
      }
      container.addClass('open');
      container.find('.js-modal-header').text(header);
      if (useHtmlContent) {
        container.find('.js-modal-body').html(content);
      } else {
        container.find('.js-modal-body').text(content);
      }
      container.find('.js-modal-cancel').text(noBtnText);
      container.find('.js-modal-confirm').text(yesBtnText);
      container.find('.js-modal-cancel').on('tap', (e) => {
        e.preventDefault();
        Pollfish.ui.closeModal(container);
        onCancel();
      });

      container.find('.js-modal-confirm').on('tap', (e) => {
        e.preventDefault();
        Pollfish.ui.closeModal(container);
        onConfirm();
      });
    },

    closeModal(container) {
      container.removeClass('open').removeClass('vertical-button-stack');
      container.find('.js-modal-cancel').off('tap');
      container.find('.js-modal-confirm').off('tap');
    },

    showAvailableLanguages() {
      const html = $('html');
      const body = $('body');
      Raven.captureBreadcrumb({
        message: 'ShowAvailableLanguages',
        category: 'ShowAvailableLanguages',
      });

      // will reset offerwall html otherwise
      html
        .animate({ scrollTop: 0 }, 10)
        .promise()
        .done(() => {
          const container = $('#language-select-modal');
          // delete previous modals
          container.empty();
          const currentPage = body.attr('data-page');
          const modal = new LanguageSelectModal(container, () => {
            // on different language select
            Pollfish.ui.renderLanguageSelect();
            if (currentPage.match(/(homescreen-)/)) {
              const homescreen = new Homescreen().render();
              Pollfish.ui.applyMainContentTemplate(homescreen);
            }
          });
          modal.show();
        });
    },

    renderLanguageSelect() {
      const shouldNotChangeLang = Pollfish.helper.isSingleSurvey();
      const shouldHideLang =
        !Pollfish.helper.isDevelopment() && Pollfish.helper.isDebugMode();
      if (shouldNotChangeLang || shouldHideLang) {
        return;
      }

      const container = $('#js-languageSelect');

      const currentLangKey = invoke(Pollfish, 'ui.currentLang.toLowerCase');
      const language = getLanguageForKey(currentLangKey);
      container.html(`
        <div class="select__container">
          <div class="select__img-flag">
            ${globeIcon}
          </div>
          <span class="select__text">
            ${language.slice(0, 2)}
          </span>
          <span class="chevron">
            ${chevronIcon}
          </span>
        </div>
      `);

      if (!Pollfish.helper.hasRenderedLanguageSelectOnce) {
        Pollfish.helper.hasRenderedLanguageSelectOnce = true;
        container.on('tap', () => {
          Pollfish.ui.showAvailableLanguages();
        });
      }
    },

    overridePrimaryColorClasses(primaryColor, backgroundColor) {
      Pollfish.ui.removeBrandedTheme();

      if (primaryColor) {
        // grab either white or black based on given color
        const invertedColor = Pollfish.helper.getTextColor(primaryColor);
        $('head').append(`
          <style id="branded-theme">
            .color-primary {
              color: ${primaryColor} !important;
            }

            .btn-primary,
            .btn-w-icon {
              color: ${invertedColor};
              background-color: ${primaryColor} !important;
            }

            .answer.answerClick .answer__description,
            .answerClick.matrix__answer .answer__description,
            .answer--click .answer__description,
            .answer.answerClick:not(.answer--with-image),
            .answerClick.matrix__answer:not(.answer--with-image),
            .answer--click:not(.answer--with-image) {
              color: ${primaryColor};
              background-color: ${primaryColor}11;
            }

            .answer.answer--with-image.answerClick,
            .answer--with-image.answerClick.matrix__answer,
            .answer--click.answer--with-image {
              border-color: ${primaryColor};
            }

            .answer.answerClick .answer__description .answer__description:before,
            .answerClick.matrix__answer .answer__description .answer__description:before,
            .answer.answerClick .answer__description:before,
            .answerClick.matrix__answer .answer__description:before,
            .answer--click .answer__description .answer__description:before,
            .answer--click .answer__description:before,
            .answer.answerClick:not(.answer--with-image) .answer__description:before,
            .answerClick.matrix__answer:not(.answer--with-image) .answer__description:before,
            .answer.answerClick:not(.answer--with-image):before,
            .answerClick.matrix__answer:not(.answer--with-image):before,
            .answer--click:not(.answer--with-image) .answer__description:before,
            .answer--click:not(.answer--with-image):before,
            .matrix__selected-answer {
              color: ${primaryColor};
            }

            .qp-bar__number {
              color: ${primaryColor};
            }

            .qp-bar__line #progressBar {
              background-color: ${primaryColor};
            }

            .ui-btn-active,
            .ranking__answer-btn--selected {
              background-color: ${primaryColor} !important;
            }

            #wrapper {
              background-color: ${backgroundColor};
            }

            .scale-label.active,
            .scale-step.active,
            .ui-slider-value,
            .ui-btn-inner .ui-btn-text:before,
            .ui-btn-inner .ui-btn-text:after {
              color: ${primaryColor};
            }
            .ui-btn-inner {
              border-color: ${primaryColor};
            }


            .bipolar-horizontal-column.column-selected:before,
            .bipolar-vertical-column.column-selected:before {
              color: ${primaryColor};
            }

            .bipolar-vertical--checked:before {
              color: ${primaryColor};
            }

            .bipolar-slider-step,
            .bipolar-slider .ui-slider-handle {
              border-color: ${primaryColor};
            }
            .bipolar-slider .ui-slider-handle--touched {
              background-color: ${primaryColor};
            }
            .bipolar-slider .ui-slider-bg {
              background-color: #E1E6EC !important;
            }

            .scrollbox {
              background-image:
                /* Shadows */
                linear-gradient(to right, ${backgroundColor}, ${backgroundColor}),
                linear-gradient(to right, ${backgroundColor}, ${backgroundColor}),
                /* Shadow covers */
                linear-gradient(to right, rgba(0,0,0,.1), rgba(255,255,255,0)),
                linear-gradient(to left, rgba(0,0,0,.1), rgba(255,255,255,0));
            }

          </style>
        `);
      }

      if (backgroundColor) {
        const backgroundInverted = Pollfish.helper.getTextColor(
          backgroundColor,
        );
        const rankingOpacity = backgroundInverted === '#000000' ? '0.3' : '0.7';
        $('head').append(`
          <style id="branded-theme-2">
            #wrapper {
              background-color: ${backgroundColor};
            }

            .ranking__answer-btn {
              background-color: ${backgroundInverted};
              color: ${backgroundColor};
            }

            .matrix__question,
            .matrix:before,
            .answers-wrapper--description,
            .scale-step,
            .scale-step.closer,
            .scale-step.close,
            .ranking__title,
            .ranking__answers-high,
            .ranking__answers-low,
            .scale-step.sticky,
            .scale-label,
            .ranking__title {
              color: ${backgroundInverted};
            }
            .ranking__answer-btn--disabled {
              opacity: ${rankingOpacity};
            }
            .matrix__hide-answers-btn {
              color: ${backgroundInverted};
              border-color: ${backgroundInverted} !important;
            }

            .unlock-screen--title h1,
            .unlock-screen--title p,
            .unlock-screen--acceptance-info,
            .footer__terms {
              color: ${backgroundInverted};
            }

            [data-page="gdpr"] h1,
            [data-page="gdpr"] h2,
            [data-page="gdpr"] p,
            [data-page="gdpr"] li {
              color: ${backgroundInverted};
            }

            .gdpr-footer--outer .gdpr-btn-container h1 {
              color: unset;
            }
          </style>
        `);
      }
    },

    applyBrandedSurveyStyles() {
      const applyPrimaryColorClasses = () => {
        const whiteLabelEnabled = Pollfish.helper.whiteLabelEnabled();
        const logoUrl = whiteLabelEnabled
          ? Pollfish.helper.json.theme_logo
          : '';
        const surveyTheme = {
          logoUrl,
          advancedThemeSettingsEnabled: whiteLabelEnabled,
          hideLogo: !logoUrl,
        };
        applyLogoChanges(surveyTheme);

        Pollfish.ui.overridePrimaryColorClasses(
          Pollfish.helper.json.theme_primary_color,
          Pollfish.helper.json.theme_background_color,
        );
      };

      const surveyTheme = Pollfish.helper.getSurveyTheme();
      const surveyThemeWithSettings = Pollfish.helper.getSurveyThemeWithSettings();
      const isDefaultTheme = get(surveyThemeWithSettings, 'isDefault', false);
      const hasThemeParamsInUrl = Pollfish.helper.isBrandedSurveyFromParams();

      if (surveyTheme && (hasThemeParamsInUrl || !isDefaultTheme)) {
        const isPollfishTheme = Pollfish.helper.hasPollfishSurveyTheme();
        if (isPollfishTheme && isDefaultTheme && !hasThemeParamsInUrl) {
          // Default Pollfish theme with no url params
          applyPrimaryColorClasses();
        } else if (
          isPollfishTheme &&
          !isDefaultTheme &&
          (Pollfish.helper.json.theme_primary_color ||
            Pollfish.helper.json.theme_background_color)
        ) {
          // it's a non default Pollfish theme. i.e. Dark theme
          applyPrimaryColorClasses();
        } else {
          const hasAdvancedThemeColors = get(
            surveyThemeWithSettings,
            'advancedThemeSettingsEnabled',
            false,
          );
          overrideBrandUI(surveyTheme, hasAdvancedThemeColors);
        }
      } else {
        applyPrimaryColorClasses();
      }

      // Note: header padding is added for previewing mobile when on diy (Design request)
      const headerPaddingTop = Pollfish.helper.isPreview ? '12px' : 0;
      const headerEl = document.querySelector('.header');
      if (headerEl) {
        headerEl.style.paddingTop = headerPaddingTop;
      }
    },

    removeBrandedTheme() {
      const existingBrandedTheme = document.querySelector('#branded-theme');
      const existingBrandedTheme2 = document.querySelector('#branded-theme-2');
      if (existingBrandedTheme) {
        existingBrandedTheme.remove();
      }
      if (existingBrandedTheme2) {
        existingBrandedTheme.remove();
      }
    },

    // check if answer is valid and qualifies to next question
    isQuestionValid(question, answers) {
      if (
        Pollfish.ui.validRanking(question) &&
        Pollfish.ui.validMatrix(question) &&
        Pollfish.ui.validMinMaxNumberOfAnswers(question) &&
        Pollfish.ui.validMinMaxValue(question) &&
        Pollfish.ui.validMatrixVanWestendorp(question) &&
        Pollfish.ui.validOtherValue(question) &&
        Pollfish.ui.validMatrixMaxDiff(question) &&
        Pollfish.ui.validMatrixBipolar(question) &&
        isDrilldownValid(question, answers) &&
        isOpenEndedNumericOrConstantSumValid(question) &&
        isOpenEndedValid(question) &&
        isConversationalAiValid(question)
      ) {
        return true;
      }
      return false;
    },

    // check if other answer can be empty or not when selected
    // and question type is single on multiple select
    validOtherValue(question) {
      // if its a single or multiple question run validations
      // however if it is not return true to qualify
      if (
        question.style !== QUESTION_SINGLE &&
        question.style !== QUESTION_MULTIPLE
      ) {
        return true;
      }
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
      // find the other answer and check for the property
      const otherAnswer = find(question.answers, ['style', ANSWER_OTHER]);
      const otherRequired = get(otherAnswer, 'required', false);
      if (!otherRequired) {
        return true;
      }
      // if the other answer is not selected then return true to qualify
      // scope the search to the current question wrapper to avoid affecting other questions
      const otherAnswerElement = $(questionWrapper).find('.answer-other');
      const isOtherSelected = otherAnswerElement.hasClass('answerClick');
      if (!isOtherSelected) {
        return true;
      }
      // if the other answer is selected and the value is not empty
      // then return true to qualify
      const otherValue = Pollfish.helper.getTextFromField(
        `#otherContainerInput-${question.refId || question.id}`,
      );
      if (otherValue) {
        return true;
      }
      // show warning message for empty other answer and do not qualify
      Pollfish.ui.showValidationMessage(
        questionWrapper,
        Pollfish.ui.getText('warnings.other_open_ended'),
      );
      Pollfish.ui.scrollToElementPosition(otherAnswerElement);
      return false;
    },

    // check if min and max value in numerical open-ended is valid
    validMinMaxValue(question) {
      const questionRefId = question.refId || question.id;
      // if its a numerical open-ended question run validations
      // however if it is not return true to qualify
      if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
        const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
        const inputVal = Pollfish.helper.getNumericValue(questionRefId);
        if (isNaN(inputVal)) {
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            Pollfish.ui.getText('warnings.numeric_open'),
          );
          return false;
        }

        if (question.min !== undefined && inputVal < question.min) {
          // show prompt for a couple of seconds for minimum accepted value
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            `${Pollfish.ui.getText('warnings.numeric_with_min')} ${
              question.min
            }`,
          );
          return false;
        }

        if (question.max !== undefined && inputVal > question.max) {
          // show prompt for a couple of seconds for maximum accepted value
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            `${Pollfish.ui.getText('warnings.numeric_with_max')} ${
              question.max
            }`,
          );
          return false;
        }

        const { allowedDecimals = 0 } = question;
        const [, decimals = ''] = inputVal.toString().split('.');
        const numberOfDecimalsInInput = decimals.length;
        if (allowedDecimals === 0 && numberOfDecimalsInInput > 0) {
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            Pollfish.ui.getText('warnings.numeric_integer_only'),
          );
          return false;
        }
        if (numberOfDecimalsInInput > allowedDecimals) {
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            Pollfish.ui.getText(
              'warnings.max_decimals',
              allowedDecimals.toString(),
            ),
          );
          return false;
        }
        return true;
      }
      return true;
    },

    // check if min and max number of answers in multiple choice question is valid
    validMinMaxNumberOfAnswers(question) {
      // if its a multiple choice question run validations
      // however if it is not return true to qualify
      if (question.style === QUESTION_MULTIPLE) {
        const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
        // when none of the above is selected we ignore the limits
        if (Pollfish.event.noneOfTheAboveSelected(questionWrapper)) {
          return true;
        }
        // when an exclusive answer is selected we ignore the limits
        if (
          $(questionWrapper).find('.answerClick.exclusive-answer').length === 1
        ) {
          return true;
        }
        const { min, max } = question;
        const numberOfAnswers = $(questionWrapper).find('.answerClick').length;
        const needsExactAnswers = question.min === question.max;
        if (min !== undefined && needsExactAnswers && numberOfAnswers !== max) {
          // show prompt for a couple of seconds to inform about exact number of answers

          if (numberOfAnswers > max) {
            Pollfish.ui.showValidationMessage(
              questionWrapper,
              `${Pollfish.ui.getText(
                'warnings.multiple_with_max',
              )} ${max} ${Pollfish.ui.getText('warnings.answers')}`,
            );
          } else {
            Pollfish.ui.showValidationMessage(
              questionWrapper,
              `${Pollfish.ui.getText(
                'warnings.multiple_with_min',
              )} ${min} ${Pollfish.ui.getText('warnings.answers')}`,
            );
          }

          return false;
        }
        if (min !== undefined && numberOfAnswers < min) {
          // show prompt for a couple of seconds to inform about minimum number of answers
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            `${Pollfish.ui.getText(
              'warnings.multiple_with_min',
            )} ${min} ${Pollfish.ui.getText('warnings.answers')}`,
          );

          return false;
        }

        if (max !== undefined && numberOfAnswers > max) {
          // show prompt for a couple of seconds to inform about maximum number of answers
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            `${Pollfish.ui.getText(
              'warnings.multiple_with_max',
            )} ${max} ${Pollfish.ui.getText('warnings.answers')}`,
          );

          return false;
        }

        return true;
      }
      return true;
    },

    // check if the ranking is valid in a ranking question type
    validRanking(question) {
      // if answer is ranking run validations
      // and if not return true
      if (question.style !== QUESTION_RANKING) {
        return true;
      }
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
      const domAnswers = $('.answerRanking');
      const answerWithoutRanking = find(
        domAnswers,
        (answer) => !$(answer).attr('data-selected-rank'),
      );
      if (!answerWithoutRanking) {
        return true;
      }
      Pollfish.ui.showValidationMessage(
        questionWrapper,
        Pollfish.ui.getText('warnings.ranking'),
      );
      Pollfish.ui.scrollToElementPosition(answerWithoutRanking);

      return false;
    },

    // check if the selections are valid in a matrix question type
    validMatrix(question) {
      // if answer is matrix single choice run validations
      // and if not return true
      if (isMatrixTypeLikert(question)) {
        const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
        const answerIds = [];
        const emptyAnswers = [];

        const matrixAnswers = applyVisibilityActionOnNodeAnswers({
          refId: question.refId,
          type: question.style,
          list: carryForwardService.getCarriedForwardAnswers(
            question,
            Pollfish.store.getRespondentKnowledge(),
            [
              ...Pollfish.screening.getQuestions(),
              ...Pollfish.survey.props.questions,
            ],
          ),
          listType: ActionVisibilityValueType.Row,
        });

        // const matrixScorepoints = Pollfish.helper.getCarriedForwardScorepoints(question);
        // iterate over all answers and get ranking values

        each($('.answerMatrix'), (domElement) => {
          const answerId = $(domElement).attr('data-answer-id');
          const selectedScorepointId = $(domElement).attr(
            'data-selected-scorepoint-id',
          );
          const belongsToAvailableAnswers = matrixAnswers.find(
            ({ id }) => id.toString() === answerId,
          );

          if (!belongsToAvailableAnswers) return;

          if (selectedScorepointId) {
            answerIds.push(selectedScorepointId);
          } else {
            emptyAnswers.push(domElement);
          }
        });

        // uniques should be the same as the answer length to make sure all are ranked
        // if it is less than the total answers length then prompt user to fix it
        if (answerIds.length < matrixAnswers.length) {
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            Pollfish.ui.getText('warnings.matrix_single'),
          );

          // get the first answer without ranking
          const answerWithoutSelection = emptyAnswers.shift();
          Pollfish.ui.scrollToElementPosition(answerWithoutSelection);

          return false;
        }
        return true;
      }
      return true;
    },

    // check if the selections are valid in a matrix van westendorp question type
    validMatrixVanWestendorp(question) {
      // if answer is matrix van westendorp run validations
      // and if not return true
      if (question.style !== QUESTION_MATRIX_VAN_WESTENDORP) {
        return true;
      }
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);

      const options = get(
        question,
        'matrix.validation.vanWestendorpValidation',
        {},
      );
      const min = options.min || VAN_WESTENDORP_QUESTION_MIN_PRICE;
      const max = options.max || VAN_WESTENDORP_QUESTION_MAX_PRICE;
      const ordering =
        options.ordering || VAN_WESTENDORP_QUESTION_SORT_ASCENDING;
      const allowedDecimals =
        options.decimals || VAN_WESTENDORP_QUESTION_DECIMALS;
      const matrixAnswersLength = $('.answerMatrixVanWestendorp').length;
      const answersDomElements = [];
      const answerIds = [];
      const emptyAnswers = [];

      // iterate over all answers and get typed values
      const answerMatrixVanWestendorp = $(questionWrapper).find(
        '.answerMatrixVanWestendorp',
      );
      each(answerMatrixVanWestendorp, (domElement) => {
        answersDomElements.push(domElement);
        const answerText = $(domElement)
          .find('.input-van-westendorp')
          .val();

        if (!isNaN(parseFloat(answerText))) {
          answerIds.push(answerText);
        } else {
          emptyAnswers.push(domElement);
        }
      });

      // all inputs should have a value type if not show a prompt
      // and then scroll the view to the first empty input
      if (answerIds.length < matrixAnswersLength) {
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getText('matrixVanWestendorp.warnings.missingValue'),
        );

        // get the first answer without value
        const answerWithoutSelection = emptyAnswers.shift();
        Pollfish.ui.scrollToElementPosition(answerWithoutSelection);
        Pollfish.ui.matrixAnswerSlideDown(answerWithoutSelection);
        return false;
      }

      const answersAsNumbers = answerIds.map((answerValue) =>
        parseFloat(answerValue),
      );
      const notValidAnswerIndexMin = answersAsNumbers.findIndex(
        (answerValue) => answerValue < min,
      );
      if (notValidAnswerIndexMin > -1) {
        const minWithDecimals = roundNumberToStringWithDecimals(
          min,
          allowedDecimals,
        );
        // show prompt for a couple of seconds for minimum accepted value
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getText(
            'matrixVanWestendorp.warnings.min',
            minWithDecimals,
          ),
        );
        Pollfish.ui.scrollToElementPosition(
          answersDomElements[notValidAnswerIndexMin],
        );
        Pollfish.ui.matrixAnswerSlideDown(
          answersDomElements[notValidAnswerIndexMin],
        );
        return false;
      }

      const notValidAnswerIndexMax = answersAsNumbers.findIndex(
        (answerValue) => answerValue > max,
      );
      if (notValidAnswerIndexMax > -1) {
        const maxWithDecimals = roundNumberToStringWithDecimals(
          max,
          allowedDecimals,
        );
        // show prompt for a couple of seconds for maximum accepted value
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getText(
            'matrixVanWestendorp.warnings.max',
            maxWithDecimals,
          ),
        );
        Pollfish.ui.scrollToElementPosition(
          answersDomElements[notValidAnswerIndexMax],
        );
        Pollfish.ui.matrixAnswerSlideDown(
          answersDomElements[notValidAnswerIndexMax],
        );
        return false;
      }

      let invalidAnswerIndex = null;
      let translationKey = 'matrixVanWestendorp.warnings.orderAsc';
      if (ordering === VAN_WESTENDORP_QUESTION_SORT_DESCENDING) {
        translationKey = 'matrixVanWestendorp.warnings.orderDesc';
        answersAsNumbers.forEach((answerValue, index) => {
          if (index === 0 || invalidAnswerIndex !== null) {
            return;
          }
          if (answersAsNumbers[index] >= answersAsNumbers[index - 1]) {
            invalidAnswerIndex = index;
          }
        });
      } else {
        answersAsNumbers.forEach((answerValue, index) => {
          if (index === 0 || invalidAnswerIndex !== null) {
            return;
          }
          if (answersAsNumbers[index] <= answersAsNumbers[index - 1]) {
            invalidAnswerIndex = index;
          }
        });
      }

      if (invalidAnswerIndex === null) {
        return true;
      }

      Pollfish.ui.showValidationMessage(
        questionWrapper,
        Pollfish.ui.getText(translationKey),
      );
      Pollfish.ui.scrollToElementPosition(
        answersDomElements[invalidAnswerIndex],
      );
      Pollfish.ui.matrixAnswerSlideDown(answersDomElements[invalidAnswerIndex]);
      return false;
    },

    // check if the selections are valid in a matrix max diff question type
    validMatrixMaxDiff(question) {
      // if answer is matrix max diff run validations
      // and if not return true
      if (question.style !== QUESTION_MATRIX_MAXDIFF) {
        return true;
      }
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);

      const bestElements = $('.answerClick[data-answer-best="true"]');
      const worstElements = $('.answerClick[data-answer-worst="true"]');

      const textLabelBest = get(question, 'matrix.columns[0].beforeLabel', '');
      const textLabelWorst = get(question, 'matrix.columns[1].beforeLabel', '');
      const translation = Pollfish.ui.getText('warnings.matrixMaxDiff', {
        best: textLabelBest,
        worst: textLabelWorst,
      });

      // respondent can select only one best answer
      if (bestElements.length !== 1) {
        Pollfish.ui.showValidationMessage(questionWrapper, translation);
        return false;
      }

      // respondent can select only one worst answer
      if (worstElements.length !== 1) {
        Pollfish.ui.showValidationMessage(questionWrapper, translation);
        return false;
      }

      const bestElement = $(bestElements).first();
      const worstElement = $(worstElements).first();

      // an answer cannot be best and worst simultaneously
      if ($(bestElement).attr('id') === $(worstElement).attr('id')) {
        Pollfish.ui.showValidationMessage(questionWrapper, translation);
        return false;
      }

      return true;
    },

    // check if the selections are valid in a matrix bipolar question type
    validMatrixBipolar(question) {
      // if answer is matrix bipolar run validations
      // and if not return true
      if (question.style !== QUESTION_MATRIX_BIPOLAR) {
        return true;
      }
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);

      const answersDomElements = [];
      const emptyAnswers = [];

      each($('.answer--bipolar'), (domElement) => {
        answersDomElements.push(domElement);
        const columnId = $(domElement).attr('data-selected-column-id');

        if (isNaN(parseInt(columnId, 10))) {
          emptyAnswers.push(domElement);
        }
      });

      // all question answers should have a value if not show a prompt
      // and then scroll the view to the first empty input
      if (emptyAnswers.length > 0) {
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getText('warnings.matrix_bipolar'),
        );

        // get the first answer without value
        const answerWithoutSelection = emptyAnswers.shift();
        Pollfish.ui.scrollToElementPosition(answerWithoutSelection);
        Pollfish.ui.matrixBipolarAnswerSlideDown(answerWithoutSelection);
        return false;
      }

      return true;
    },

    matrixAnswerSlideDown(element) {
      setTimeout(() => {
        $(element)
          .find('.matrix__answers')
          .removeClass('matrix__answers--closed')
          .slideDown();

        $(element)
          .closest('.matrix')
          .removeClass('matrix--closed');
      }, 300);
    },

    matrixBipolarAnswerSlideDown(element) {
      setTimeout(() => {
        $(element)
          .find('.bipolar-vertical-wrapper')
          .removeClass('bipolar-vertical-wrapper--closed')
          .slideDown();

        $(element)
          .closest('.bipolar-vertical')
          .removeClass('bipolar-vertical--closed');
      }, 300);
    },

    matrixOpenEndedAnswerSlideDown(element) {
      setTimeout(() => {
        $(element)
          .closest('.answer--matrix-open-ended-content-wrapper')
          .slideDown();

        $(element)
          .parents('.answer--matrix-open-ended')
          .find('.answer--matrix-open-ended-row-label')
          .removeClass('answer--matrix-open-ended-row-label--closed');
      }, 300);
    },

    scrollToElementPosition(element, timeout = 300, forceScroll = false) {
      const questionWrapper = Pollfish.ui.getClosestQuestionWrapperElement(
        element,
      );
      if (
        !forceScroll &&
        Pollfish.ui.isQuestionDisplaySamePage(questionWrapper)
      ) {
        return;
      }

      setTimeout(() => {
        const newPosition =
          $(element).position().top - $('.header').position().top - 5;
        $('html, body').animate({ scrollTop: newPosition }, 100);

        const openEndedMatrixWrapper = $(element).parents(
          '.answer--matrix-open-ended-horizontal',
        );
        // if it is an open ended matrix horizontal then scroll the element position
        if (openEndedMatrixWrapper) {
          $(openEndedMatrixWrapper).animate(
            {
              scrollLeft:
                $(element).position().left +
                openEndedMatrixWrapper.scrollLeft() -
                12,
            },
            600,
          );
        }
      }, timeout);
    },

    // apply virtual taps for question with different interface
    // than single and multiple
    virtualTaps(question) {
      const questionRefId = question.refId || question.id;
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
      const freeTextValue = $.trim($(`#freeText-${questionRefId}`).val());
      const numericValue = Pollfish.helper.getNumericValue(questionRefId);
      const freeTextNumericalValue = numericValue.toString();
      // select default answer since they are not visible
      if (
        question.style === QUESTION_DESCRIPTION ||
        (question.style === QUESTION_OPEN_ENDED && freeTextValue.length > 1) ||
        (question.style === QUESTION_NUMERICAL_OPEN_ENDED &&
          freeTextNumericalValue.length > 0) ||
        question.style === QUESTION_RANKING ||
        question.style === QUESTION_MATRIX_SINGLE ||
        question.style === QUESTION_MATRIX_MULTIPLE ||
        question.style === QUESTION_MATRIX_VAN_WESTENDORP
      ) {
        if (
          question.style === QUESTION_RANKING ||
          question.style === QUESTION_MATRIX_SINGLE ||
          question.style === QUESTION_MATRIX_MULTIPLE ||
          question.style === QUESTION_MATRIX_VAN_WESTENDORP
        ) {
          questionWrapper.querySelectorAll('.answer').forEach((el) => {
            if (!el.classList.contains('answerClick')) {
              el.dispatchEvent(new MouseEvent('click'));
            }
          });
        } else {
          const firstElement = questionWrapper.querySelector('.answer');
          if (firstElement) {
            firstElement.classList.add('answerClick');
          }
        }
        // select all the answers to proceed with validation
      } else if (
        question.style === QUESTION_MATRIX_OPEN_ENDED ||
        question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
        question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
      ) {
        questionWrapper
          .querySelectorAll('.answer--matrix-open-ended')
          .forEach((el) => {
            if (!el.classList.contains('answerClick')) {
              el.classList.add('answerClick');
            }
          });
      } else if (question.style === QUESTION_CONVERSATIONAL) {
        const aiAnswers = questionWrapper.querySelectorAll(
          '.answer--conversational-ai',
        );
        each(aiAnswers, (val) => {
          if (!$(val).hasClass('answerClick')) {
            $(val).addClass('answerClick');
          }
        });
      }

      // event binded to solve ios issue with focus on textarea
      $('#wrapper').unbind('click');
      return true;
    },

    // prompt user for input
    getMissingAnswerPrompt(question) {
      if (
        question.style === QUESTION_SINGLE ||
        question.style === QUESTION_NPS
      ) {
        return Pollfish.ui.getText('warnings.single');
      }
      if (question.style === QUESTION_MULTIPLE) {
        return Pollfish.ui.getText('warnings.multiple');
      }
      if (question.style === QUESTION_RATING) {
        return Pollfish.ui.getText('warnings.rating');
      }
      if (question.style === QUESTION_SLIDER) {
        return Pollfish.ui.getText('warnings.slider');
      }
      if (question.style === QUESTION_OPEN_ENDED) {
        return Pollfish.ui.getText('warnings.open_ended');
      }
      if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
        return Pollfish.ui.getText('warnings.numeric_open');
      }
      if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
        return Pollfish.ui.getText('warnings.numeric_open');
      }
      if (question.style === QUESTION_MATRIX_MAXDIFF) {
        return Pollfish.ui.getText('warnings.single');
      }
      if (question.style === QUESTION_MATRIX_BIPOLAR) {
        return Pollfish.ui.getText('warnings.matrix_bipolar');
      }
      if (question.style === QUESTION_DRILLDOWN) {
        return Pollfish.ui.getText('warnings.multiple');
      }
      if (question.style === QUESTION_CONVERSATIONAL) {
        return Pollfish.ui.getText('conversational.validation');
      }
      return '';
    },

    triggerEnterKeyDown(element) {
      let event;
      if (typeof KeyboardEvent === 'function') {
        event = new KeyboardEvent('keydown', {
          key: 'Enter',
          keyCode: 13,
          which: 13,
          bubbles: true,
        });
      } else {
        // IE 9 & 10 fallback
        event = document.createEvent('KeyboardEvent');
        event.initKeyboardEvent(
          'keydown',
          true,
          true,
          window,
          'Enter',
          0,
          '',
          false,
          '',
        );
      }
      element.dispatchEvent(event);
    },

    validateQuestionAnswers(element) {
      if (Pollfish.ui.isSamePageValidatedOnce(element)) {
        const question = Pollfish.ui.getQuestionDataByQuestionWrapperElement(
          element,
        );
        Pollfish.ui.validateRespondentQuestionAnswers(question);
      }
    },

    getQuestionDataByQuestionWrapperElement(questionWrapper) {
      let question;
      const questionElement = $(questionWrapper);
      const questionRefId = questionElement.data('ref-id');
      const questionId = questionElement.attr('id');
      // we prefer using refIds over ids but in some cases where a question might not have a refId
      if (questionRefId) {
        question = Pollfish.survey.getQuestionByRefId(questionRefId);
      } else {
        question = Pollfish.survey.getQuestionById(questionId);
        // throw a warning only if question does not contain a refId when comes for backend
        Raven.captureMessage(
          'Fallback to questionId when it should find a question by refId',
          {
            extra: {
              questionRefId,
              question,
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
      return question;
    },

    // load template of question or the thank you screen if no question exists
    loadQuestionOrThankScreen() {
      // retrieve question
      const question = Pollfish.ui.getQuestionDataByQuestionWrapperElement(
        $('.js-question'),
      );
      const group = Pollfish.survey.getDisplaySamePageGroupByQuestion(question);
      const groupDisplaySamePage = invoke(group, 'isDisplaySamePage', false);

      if (!groupDisplaySamePage) {
        const {
          isValid = false,
          selectedAnswers = [],
          selectedAnswerIds = [],
        } = Pollfish.ui.validateRespondentQuestionAnswers(question);

        if (!isValid) {
          return;
        }

        Pollfish.ui.cleanElementsAfterValidation();
        Pollfish.ui.loadQuestionOrThankScreenForNonSamePageQuestion({
          question,
          selectedAnswers,
          selectedAnswerIds,
        });
      }

      if (groupDisplaySamePage) {
        // TODO: achil GO to every question and validate if some of the questions goes to the end of survey
        const questions = get(group, 'questions', []);
        const questionDataAfterValidation = [];
        let firstInvalidQuestion = null;
        let atLeastOneInvalid = false;

        questions.forEach((questionData) => {
          const {
            isValid = false,
            selectedAnswers = [],
            selectedAnswerIds = [],
          } = Pollfish.ui.validateRespondentQuestionAnswers(questionData);

          if (!atLeastOneInvalid && !isValid) {
            firstInvalidQuestion = questionData;
            atLeastOneInvalid = true;
          }

          questionDataAfterValidation.push({
            isValid,
            refId: questionData.refId,
            selectedAnswers,
            selectedAnswerIds,
          });
        });

        if (atLeastOneInvalid) {
          Pollfish.ui.stickySubmitButton();
          const questionWrapper = Pollfish.ui.getQuestionWrapperElement(
            firstInvalidQuestion,
          );
          Pollfish.ui.scrollToElementPosition(questionWrapper, 300, true);
          return;
        }

        const saveAnswerObjectsForRequest = [];
        questionDataAfterValidation.forEach((questionData) => {
          const foundQuestion = find(questions, ['refId', questionData.refId]);
          const requestObject = Pollfish.survey.saveAnswer(
            foundQuestion,
            questionData.selectedAnswers,
          );
          if (requestObject) {
            saveAnswerObjectsForRequest.push(
              transformValuesToString(requestObject),
            );
          }
        });
        Pollfish.survey.saveGroupAnswerRequest({
          multiQuestionAnswers: saveAnswerObjectsForRequest,
          groupId: group.id,
          displaySamePage: groupDisplaySamePage,
        });

        const lastIndex = questions.length - 1;
        Pollfish.ui.cleanElementsAfterValidation();
        Pollfish.ui.getNextQuestionOrFinish(
          questions[lastIndex],
          questionDataAfterValidation[lastIndex].selectedAnswers,
          questionDataAfterValidation[lastIndex].selectedAnswerIds,
        );
      }
    },

    cleanElementsAfterValidation() {
      // we only destroy the slider after validating.
      // Or else the user won't be able to answer again
      invoke(Pollfish, 'ui.slider.destroy');
      // we only destroy the bipolar after validating.
      // Or else the user won't be able to answer again.
      invoke(Pollfish, 'ui.bipolar.destroy');
    },

    loadQuestionOrThankScreenForNonSamePageQuestion({
      question,
      selectedAnswers,
      selectedAnswerIds,
    }) {
      const requestObject = Pollfish.survey.saveAnswer(
        question,
        selectedAnswers,
      );
      if (requestObject) {
        Pollfish.survey.saveAnswerRequest(requestObject);
      }

      if (!Pollfish.survey.getCheckAndEnterSurveyHasBeenCalledOnce()) {
        checkAndEnterSurvey({
          isUserNotEligible: false,
          onSurveyContinue: () => {
            // We need to change the language to the survey language now...
            const userLang = Pollfish.helper.json.user_lang;
            const surveyLang = Pollfish.helper.json.lang;
            if (surveyLang && userLang !== surveyLang) {
              Pollfish.helper.changeLanguage(surveyLang);
            }
            Pollfish.ui.getNextQuestionOrFinish(
              question,
              selectedAnswers,
              selectedAnswerIds,
            );
          },
        });
        return;
      }
      Pollfish.ui.getNextQuestionOrFinish(
        question,
        selectedAnswers,
        selectedAnswerIds,
      );
    },

    getNextQuestionOrFinish(question, selectedAnswers) {
      Pollfish.survey
        .getNextQuestion(selectedAnswers, question)
        .then((nextQuestion) => {
          if (nextQuestion) {
            // transfer user to the top to check new question
            Pollfish.ui.moveToQuestion(nextQuestion);
            return;
          }

          if (Pollfish.survey.isFeedbackSurvey()) {
            Pollfish.helper.handleSurveyReject();
            return;
          }

          Pollfish.surveyState.setState(SURVEY_STATE.COMPLETING_SURVEY);
          Pollfish.helper
            .handleSurveyComplete({
              isUserNotEligible: false,
              shouldGetInRouter:
                Pollfish.helper.isSingleSurvey() &&
                !Pollfish.helper.isPreview &&
                !Pollfish.helper.isDebugMode() &&
                !Pollfish.helper.isRouterDisabled(),
            })
            .catch(Pollfish.helper.handleGlobalPromiseError);
        })
        .catch(Pollfish.helper.handleUnsuccesfullGroupSetup);
    },

    validateRespondentQuestionAnswers(question) {
      const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
      // remove padding bottom class
      $(window).off('resize');
      $('.wrapper__holder').removeClass('open-keyboard');
      $('#promptInfo').removeClass('prompt-info--visible');

      if (Pollfish.ui.isQuestionDisplaySamePage(questionWrapper)) {
        Pollfish.ui.setSamePageValidatedOnce(questionWrapper);
      }

      // check if video exists and user has seen the full video
      // in order to qualify for next question
      if (!Pollfish.ui.hasUserSeenAllVideos(question)) {
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getText('warnings.video'),
        );
        return { isValid: false };
      }

      // check if video/audio exists and user has seen/heard the full video/audio
      // in order to qualify for next question (AB Testing)
      if (!isEmpty(question.selectedAttributes)) {
        const { groups } = Pollfish.survey;
        let group;
        let selectedAttributes;
        let referenceAttribute;
        if (!isEmpty(groups)) {
          group = groups.find(({ refId }) => question.groupRefId === refId);
        }
        if (!isEmpty(group)) {
          selectedAttributes = group.getAttributesForQuestion(question.refId);
        }
        if (!isEmpty(selectedAttributes)) {
          referenceAttribute = selectedAttributes.find(
            ({ media }) =>
              media && INTERACTIVE_MEDIA_TYPES.includes(media.type),
          );
        }
        if (referenceAttribute && !Pollfish.ui.hasUserSeenAllVideos(question)) {
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            Pollfish.ui.getText('warnings.video'),
          );
          return { isValid: false };
        }
      }

      // apply virtual taps for question with different interface
      // than single and multiple. Effectively means "add .answerClick classes
      // where needed so that the next for loop will work as expected :P"
      Pollfish.ui.virtualTaps(question);

      const selectedAnswerIds = [];
      const selectedAnswers = [];

      for (
        let i = 0;
        i < $(questionWrapper).find('.answerClick').length;
        i += 1
      ) {
        const element = $(questionWrapper).find('.answerClick')[i];
        const answerId = $(element).attr('id');

        selectedAnswerIds.push(answerId);
        selectedAnswers.push(Pollfish.survey.getAnswerById(answerId, question));
        // if question type is not multiple always break and don't allow more selected answers
        // this is as a fallback to make sure things don't break
        // TODO: remove this and handle it differently
        if (
          question.style !== QUESTION_MULTIPLE &&
          question.style !== QUESTION_RANKING &&
          question.style !== QUESTION_MATRIX_SINGLE &&
          question.style !== QUESTION_MATRIX_MULTIPLE &&
          question.style !== QUESTION_MATRIX_VAN_WESTENDORP &&
          question.style !== QUESTION_MATRIX_MAXDIFF &&
          question.style !== QUESTION_MATRIX_BIPOLAR &&
          question.style !== QUESTION_MATRIX_OPEN_ENDED &&
          question.style !== QUESTION_MATRIX_OPEN_ENDED_NUMERIC &&
          question.style !== QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM &&
          question.style !== QUESTION_CONVERSATIONAL
        ) {
          break;
        }
      }

      // if no answer is selected don't qualify to next question
      // and prompt user for input
      if (isEmpty(selectedAnswers)) {
        const userInputAI = document.querySelector('.user-input-ai');
        if (userInputAI && !isEmpty(userInputAI.value)) {
          Pollfish.ui.triggerEnterKeyDown(userInputAI);
          return { isValid: false };
        }

        // prompt user for input
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getMissingAnswerPrompt(question),
        );

        return { isValid: false, selectedAnswerIds, selectedAnswers };
      }

      if (question.style === QUESTION_CONVERSATIONAL) {
        const userInputAI = document.querySelector('.user-input-ai');
        const ongoingConversation = document.querySelector(
          `.answer--conversational-ai[data-status="${CONVERSATIONAL_AI_VALUE_STATUSES.ONGOING_CONVERSATION}"]`,
        );
        if (ongoingConversation) {
          if (userInputAI && !isEmpty(userInputAI.value)) {
            Pollfish.ui.triggerEnterKeyDown(userInputAI);
            return { isValid: false, selectedAnswerIds, selectedAnswers };
          }
          Pollfish.ui.showValidationMessage(
            questionWrapper,
            Pollfish.ui.getMissingAnswerPrompt(question),
          );
          return { isValid: false, selectedAnswerIds, selectedAnswers };
        }
      }

      const answerErrors = Survey.validateAnswers(
        question.refId || question.id,
        selectedAnswers,
      );

      if (answerErrors.emptyOtherAnswer) {
        Pollfish.ui.showValidationMessage(
          questionWrapper,
          Pollfish.ui.getText('warnings.open_ended'),
        );
        return { isValid: false, selectedAnswerIds, selectedAnswers };
      }
      // TODO: move question validations per question type outside ui file
      if (!Pollfish.ui.isQuestionValid(question, selectedAnswers)) {
        return { isValid: false, selectedAnswerIds, selectedAnswers };
      }

      Pollfish.ui.clearValidationMessage(questionWrapper);
      return {
        isValid: true,
        selectedAnswerIds,
        selectedAnswers,
      };
    },

    showValidationMessage(questionElement, message) {
      if (Pollfish.ui.isQuestionDisplaySamePage(questionElement)) {
        Pollfish.ui.setSamePageIsValidAnswers(questionElement, false);
        Pollfish.ui.showAnsweredQuestionsInSubmitButton();
        $(questionElement)
          .find('.validation-message')
          .html(message);
        return;
      }

      Pollfish.ui.prompt(message);
    },

    clearValidationMessage(questionElement) {
      if (Pollfish.ui.isQuestionDisplaySamePage(questionElement)) {
        Pollfish.ui.setSamePageIsValidAnswers(questionElement);
        Pollfish.ui.showAnsweredQuestionsInSubmitButton();
        $(questionElement)
          .find('.validation-message')
          .html('');
      }
    },

    // transfer user to the top to check new question
    moveToQuestion(nextQuestion) {
      // $('#wrapper').off('tap', '#submit-btn');
      $('#wrapper').off('tap', '.answer');
      $('#wrapper').off('tap', '.rating-stars__star');
      $('#wrapper').off('tap', '.matrix__answer');
      $('#wrapper').off('tap', '.matrix__question');
      $('#wrapper').off('tap', '.matrix__hide-answers-btn');
      $('#wrapper').off('tap', '.ranking__answer-btn');
      $('#wrapper').off(
        'tap',
        '.country-open .country-code, .ignore-collapse .country-code',
      );
      $('#wrapper').off('tap', '.country-header');
      $('#wrapper').off('keyup', '.input-van-westendorp');
      $('#wrapper').off('blur', '.input-van-westendorp');

      // not sure if mount/unmount is always a good idea but i don't know what kind of side effects
      // will exist when jQuery also affects the same root
      Pollfish.ui.unmountReactRoot();
      Pollfish.ui.removeStickySubmitButton();

      setTimeout(() => {
        $('#wrapper').scrollTop(0);
        $('html').scrollTop(0);
      }, 10);

      const group = Pollfish.survey.getDisplaySamePageGroupByQuestion(
        nextQuestion,
      );

      Pollfish.ui.applyTemplate(group || nextQuestion);
    },

    // intermediate screen for loading purposes
    loading() {
      Pollfish.analytics.sendEvent('general', 'router', 'Router');
      const wrapper = $('html');

      wrapper.animate({ scrollTop: 0 }, 10, () => {
        const content = FetchAdditionalSurveysScreen({
          lastPayload: Pollfish.store.lastPayload,
        });
        Pollfish.ui.applyMainContentTemplate(content);
        $('body').attr('data-page', 'loading');
      });
    },

    getSpinner(props) {
      return Loading(props);
    },

    showOverlaySpinner() {
      const content = Loading();
      const wrapper = $('#overlay-spinner');
      wrapper.addClass('open');
      wrapper.html(content);
    },

    hideOverlaySpinner() {
      setTimeout(() => {
        const wrapper = $('#overlay-spinner');
        wrapper.removeClass('open');
        wrapper.html('');
      }, 0);
    },

    showSpinner(texts) {
      const wrapper = $('html');
      const scrollDuration = Pollfish.helper.isCypressEnv() ? 0 : 10;
      // NOTE: this is buggy. If anything renders with a delay less than 10ms
      // this will overwrite everything. That is why we return a promise, to indicate
      // when an action can actually happen
      return new Promise((resolve) => {
        wrapper.animate({ scrollTop: 0 }, scrollDuration, () => {
          const content = Loading(texts);
          Pollfish.ui.applyMainContentTemplate(content);
          $('body').attr('data-page', 'loading');
          resolve();
        });
      });
    },

    showSurveyLoading() {
      const subtitle = Pollfish.ui.getText('offerwall.redirect.loading');
      return Pollfish.ui.showSpinner({ subtitle });
    },

    thirdPartyLoader() {
      const wrapper = $('body');
      wrapper.html('');
      wrapper.attr('data-page', 'loading');
    },

    // show thank you component on basic format
    thankYouBasic({
      isResultPending,
      reward,
      payload,
      surveyId,
      onDemandEndPageUrl,
      payloadIsFromLastPayload,
    } = {}) {
      // we need to clear the last payload since it will be used in this view
      Pollfish.store.lastPayload = undefined;

      const shouldRedirectToCustomEndPage = Boolean(
        payload &&
          payload.customEndPage &&
          surveyId &&
          !payload.redirectOnDemand,
      );
      if (shouldRedirectToCustomEndPage) {
        const redirectURL = Pollfish.helper.getRedirectURL(surveyId);
        if (redirectURL) {
          window.location.href = redirectURL;
          return;
        }
      }
      const wrapper = $('html');
      const container = $('.main-content');

      Raven.captureBreadcrumb({
        message: 'thankYouBasic',
        category: 'thankYouBasic',
      });

      // these settings are currently only expected on distributionLink surveys
      const redirectOnDemand = get(payload, 'redirectOnDemand', false);
      const endPageSettingType = get(payload, 'endPageSettingType');

      const thankYou = new ThankYouPage({
        isResultPending,
        reward,
        surveyId,
        redirectOnDemand,
        endPageSettingType,
        onDemandEndPageUrl,
        rewardIsFromLastPayload: payloadIsFromLastPayload,
      }).render();
      container.html(thankYou);

      // deferring scrolling until the render of the thank you page has completed
      // PUB-1328
      setTimeout(() => {
        wrapper.scrollTop(0);
      }, 10);

      $('body').attr('data-page', 'thank-you');
    },

    showFeedbackSurvey() {
      Raven.captureBreadcrumb({
        message: 'feedbackSurvey',
        category: 'feedbackSurvey',
      });
      const originalFeedbackQuestion = get(
        Pollfish,
        'helper.feedbackQuestions[0]',
      );

      const feedbackQuestion = {
        ...originalFeedbackQuestion,
        answers: [...originalFeedbackQuestion.answers],
      };

      // answer other on feedback does not have label, so was rendering undefined on screen
      const { answers } = feedbackQuestion;
      const lastAnswer = answers[answers.length - 1];
      if (lastAnswer.style === ANSWER_OTHER && !lastAnswer.label) {
        lastAnswer.label = 'Please specify';
      }

      if (
        !Pollfish.helper.hasCustomSurveyTheme() &&
        !Pollfish.helper.whiteLabelEnabled()
      ) {
        // maybe pass this on to the backend
        feedbackQuestion.image_url = `${publicAssetsPrefix}/img/feedback.jpg`;
      }

      let surveySessionId;
      if (Pollfish.survey) {
        surveySessionId = Pollfish.survey.getSurveySessionId();
      }

      Pollfish.survey = new Survey(
        {
          ...feedbackSurvey,
          isFeedbackSurvey: true,
          s_id: Pollfish.helper.json.s_id,
          survey_session_id: surveySessionId,
          questions: [feedbackQuestion],
          third_party_provider: Pollfish.helper.json.third_party_provider,
          no_check: invoke(Pollfish, 'survey.getNoCheck'),
        },
        {
          checkStatus: false,
        },
      );
      Pollfish.survey.start().catch(Pollfish.helper.handleError);
    },

    showSurveyExpiredScreen(nextScreenCallback) {
      const wrapper = $('html');
      wrapper.animate({ scrollTop: 0 }, 10, () => {
        const content = FetchAdditionalSurveysScreen({
          surveyExpired: true,
          callback: nextScreenCallback,
        });

        Pollfish.ui.applyMainContentTemplate(content);
        $('body').attr('data-page', 'loading');
      });
    },

    showScreenOut({
      reason,
      reward,
      payload,
      surveyId,
      onDemandEndPageUrl,
    } = {}) {
      // we need to clear the last payload since it will be used in this view
      Pollfish.store.lastPayload = undefined;
      const wrapper = $('html');

      const shouldRedirectToCustomEndPage = Boolean(
        payload &&
          payload.customEndPage &&
          surveyId &&
          !payload.redirectOnDemand,
      );

      if (shouldRedirectToCustomEndPage) {
        const redirectURL = Pollfish.helper.getRedirectURL(surveyId);
        if (redirectURL) {
          Pollfish.mobile.interface.userNotEligible(payload);
          window.location.href = redirectURL;
          return;
        }
      }

      Raven.captureBreadcrumb({
        message: 'screenOutPage',
        category: 'screenOutPage',
      });
      const redirectOnDemand = get(payload, 'redirectOnDemand', false);
      const endPageSettingType =
        payload && redirectOnDemand && payload.endPageSettingType;

      wrapper.animate({ scrollTop: 0 }, 10, () => {
        const htmlTemplate = new ScreenOutPage({
          reason,
          reward,
          redirectOnDemand,
          endPageSettingType,
          onDemandEndPageUrl,
        }).render();

        Pollfish.ui.applyMainContentTemplate(htmlTemplate);

        // deferring scrolling until the render of the thank you page has completed
        // PUB-1328
        setTimeout(() => {
          wrapper.scrollTop(0);
          Pollfish.mobile.interface.userNotEligible(payload);
        }, 10);

        $('body').attr('data-page', 'thank-you');
      });
    },

    changeVideoVolume(videoRefId) {
      const video = document.getElementById(`video-${videoRefId}`);
      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      const volumeBar = customControls.querySelector(
        `#volume-bar-${videoRefId}`,
      );
      video.volume = volumeBar.value;
      const volumeButton = customControls.querySelector(
        `#volume-button-${videoRefId}`,
      );
      if (volumeBar.value === '0') {
        volumeButton.innerHTML = muteIcon;
      } else {
        volumeButton.innerHTML = volumeIcon;
      }
    },

    handleVolumeInputChange(e) {
      const { min, max, value } = e.target;

      // eslint-disable-next-line
      const width = ((value - min) * 100) / (max - min);
      e.target.style.backgroundSize = `${width}% 100%`;
    },

    toggleVolume(videoRefId) {
      const video = document.getElementById(`video-${videoRefId}`);
      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      const volumeBar = customControls.querySelector(
        `#volume-bar-${videoRefId}`,
      );
      const volumeButton = customControls.querySelector(
        `#volume-button-${videoRefId}`,
      );
      if (video.volume > 0) {
        video.volume = 0;
        volumeBar.value = 0;
        volumeButton.innerHTML = muteIcon;
        volumeBar.style.backgroundSize = '0% 100%';
      } else {
        video.volume = 0.5;
        volumeBar.value = 0.5;
        volumeButton.innerHTML = volumeIcon;
        volumeBar.style.backgroundSize = '50% 100%';
      }
    },

    // handle video element after user clicked
    handleVideo(videoRefId, disableControl) {
      // get video element
      const video = document.getElementById(`video-${videoRefId}`);

      $(video)
        .siblings('.video-overlay-play-button')
        .hide();

      // start playing video
      video.play();

      // enable controls for the user to pause the video
      if (!disableControl) {
        $(video).attr('controls', true);
      } else {
        const container = $(`#questionVideo-${videoRefId}-controls`);
        container.addClass('visible');
        const volumeInput = container
          .get(0)
          .querySelector('input[type="range"]');
        volumeInput.addEventListener('input', this.handleVolumeInputChange);
        const volumeButton = document.getElementById(
          `volume-button-${videoRefId}`,
        );
        volumeButton.addEventListener('click', () =>
          this.toggleVolume(videoRefId),
        );
      }

      // Hide full screen btn when device is android and has sdk version lower than 17
      Pollfish.ui.hideFullScreenBtn(video, 17);

      // attach listener to be notified if user watched the video/audio
      video.addEventListener('play', Pollfish.ui.onVideoPlay, false);

      // attach listener to be notified if user watched the video/audio
      video.addEventListener('ended', Pollfish.ui.onVideoEnd, false);

      // attach listener to be notified about video time update
      // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
      video.addEventListener(
        'timeupdate',
        Pollfish.ui.onVideoTimeUpdate,
        false,
      );
    },

    hideFullScreenBtn(video, sdkVersion) {
      return Pollfish.helper.json.sdk_version < sdkVersion &&
        Pollfish.helper.isAndroid()
        ? $(video).addClass('full-screen-btn--hidden')
        : '';
    },

    // when a video/audio start pause all the other video/audio
    onVideoPlay(event) {
      const videoRefId = $(event.target).data('video-ref-id');
      const questionRefId = $(event.target).data('question-ref-id');
      if (!isEmpty(Pollfish.store.questionVideos)) {
        let videoRefIds = Pollfish.store.questionVideos.filter(
          ({ refId, questionRefIds }) => {
            if (
              refId !== videoRefId &&
              questionRefIds.includes(questionRefId)
            ) {
              return true;
            }
            return false;
          },
        );
        videoRefIds = videoRefIds.map(({ refId }) => refId);
        videoRefIds.forEach((videoId) => {
          const video = document.getElementById(`video-${videoId}`);
          if (video) {
            video.pause();
          }
        });
      } else {
        Raven.captureException(
          'Found a video/audio playing without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
    },

    onVideoTimeUpdate(event) {
      const video = event.target;
      const videoRefId = $(video).attr('data-video-ref-id');
      const currentTime = parseInt(video.currentTime, 10);

      if (isNaN(currentTime)) {
        Raven.captureException(
          'timeUpdate found a video/audio with currentTime as NaN',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return;
      }

      // user may seeking to go forward or backwards,
      // when seeking the video is paused so we should not make any update
      if (video.paused) {
        return;
      }

      if (isEmpty(Pollfish.store.questionVideos)) {
        Raven.captureException(
          'timeUpdate found a video/audio without videos in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return;
      }

      const storedVideoIndex = Pollfish.store.questionVideos.findIndex(
        (videoInStore) => videoInStore.refId === videoRefId,
      );

      if (storedVideoIndex < 0) {
        Raven.captureException(
          'timeUpdate found a video/audio without video index existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return;
      }
      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      if (customControls) {
        const progressBar = customControls.querySelector('.video-progress-bar');
        // Work out how much of the media has played via the duration and currentTime parameters
        const percentage = Math.floor((100 / video.duration) * currentTime);

        // Update the progress bar's value
        progressBar.value = percentage;
        // Update the progress bar's text (for browsers that don't support the progress element)
        progressBar.innerHTML = `${percentage}% played`;
      }

      if (Pollfish.store.questionVideos[storedVideoIndex].seen) {
        return;
      }

      Pollfish.store.questionVideos[storedVideoIndex].secondsSeen[
        currentTime
      ] = 1;
    },

    // check if video/audio has ended
    onVideoEnd(event) {
      const videoRefId = $(event.target).attr('data-video-ref-id');
      Pollfish.mobile.interface.notifyVideoEnd();

      const customControls = document.getElementById(
        `questionVideo-${videoRefId}-controls`,
      );
      if (customControls) {
        const progressBar = customControls.querySelector('.video-progress-bar');
        progressBar.value = 100;
        // Update the progress bar's text (for browsers that don't support the progress element)
        progressBar.innerHTML = '100% played';
      }

      if (
        !Pollfish.ui.hasUserAlreadySeenVideo(videoRefId) &&
        !Pollfish.ui.hasUserSeenFullVideo(videoRefId)
      ) {
        Pollfish.ui.prompt(Pollfish.ui.getText('warnings.video'));
        return;
      }
      if (!isEmpty(Pollfish.store.questionVideos)) {
        Pollfish.store.questionVideos = map(
          Pollfish.store.questionVideos,
          (video) => {
            if (video.refId === videoRefId) {
              return { ...video, seen: true, secondsSeen: [] };
            }
            return video;
          },
        );
      } else {
        Raven.captureException(
          'Found a video/audio ended without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
    },

    // we check the array of secondsSeen of a stored video
    // and if the summary of the seconds is equal minus one second
    // to the video duration means user has seen full video
    hasUserSeenFullVideo(videoRefId) {
      const video = document.getElementById(`video-${videoRefId}`);
      const videoDuration = video.duration;

      if (isEmpty(Pollfish.store.questionVideos)) {
        Raven.captureException(
          'hasUserSeenFullVideo found a video/audio without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return false;
      }

      const storedVideo = Pollfish.store.questionVideos.find(
        (videoInStore) => videoInStore.refId === videoRefId,
      );

      if (!storedVideo) {
        Raven.captureException(
          'hasUserSeenFullVideo found a video/audio index without existing in store',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return false;
      }
      const storedVideoDuration = storedVideo.secondsSeen
        .filter((second) => !!second)
        .reduce((a, b) => a + b, 0);
      const diffInSeconds = Math.abs(storedVideoDuration - videoDuration);
      return diffInSeconds <= 1;
    },

    hasUserSeenAllVideos(question) {
      if (!question.video && isEmpty(question.conceptVideos)) {
        return true;
      }
      const questionInteractiveMedia = Pollfish.store.questionVideos.filter(
        (video) => video.questionRefIds.includes(question.refId),
      );
      if (isEmpty(questionInteractiveMedia)) {
        throw new Error(
          'Expected to find video media for question but found nothing',
        );
      }
      return questionInteractiveMedia.every((video) => video.seen === true);
    },

    hasUserAlreadySeenVideo(videoRefId) {
      const video = find(Pollfish.store.questionVideos, ['refId', videoRefId]);
      if (video) {
        return video.seen;
      }
      return false;
    },

    // display terms
    displayTerms({ showCustomTerms = false } = {}) {
      let termsUrl =
        'https://api.github.com/repos/pollfish/docs/contents/terms/respondent.md';

      if (Pollfish.helper.isDistributionLink()) {
        termsUrl =
          'https://api.github.com/repos/pollfish/docs/contents/terms/respondent-link.md';
      }

      $.ajax({
        url: termsUrl,
        headers: { Accept: 'application/vnd.github.VERSION.raw' },
      }).done((response) => {
        $('.js-github-terms').html(response.toString().wiki2html());
        $('html, body').scrollTop(0);
      });

      if (showCustomTerms) {
        const surveyId = Pollfish.helper.json.s_id;
        request({
          url: `/v2/survey/${surveyId}/terms`,
          method: 'GET',
          transformData: false,
        })
          .then(({ title, body }) => {
            const template = `
            <h3 class="custom-terms-title">${$.trim(String(title || ''))}</h3>
            <div class="custom-terms-body">${$.trim(String(body || ''))}</div>
          `;
            $('.survey-terms-content').html(template);
            $('.terms-and-conditions').addClass('with-custom-terms');

            $('.custom-terms-link > a')
              .off('tap')
              .on('tap', (event) => {
                event.preventDefault();
                const newPosition = $('#survey-terms').position().top;
                $('html, body').animate({ scrollTop: newPosition }, 100);
              });
          })
          .catch((e) => {
            if (e) {
              // eslint-disable-next-line
              console.error(e);
              Raven.captureException(e);
            }
          });
      }
    },

    // display GDPR
    displayGDPR() {
      const container = $('.main-content');
      const dimensions = Pollfish.helper.getInitialHeight();
      // NOTE: adds overhead but helps not having to check it every timprogressBar-containere.
      $('body').removeClass('gpdr--has-enough-space');
      $('body').attr('data-page', 'gdpr');
      setTimeout(() => {
        $('html, body').scrollTop(0);
      }, 10);

      // checks if device is in portrait mode
      // NOTE: This check does not need to happen more than once since the project
      //       will re-initialize itself on device resize through the SDK
      if (dimensions.height < dimensions.width) {
        container.find('.gdpr-footer--inner').removeClass('hide');
        $('body').addClass('gpdr--has-enough-space');
        if (
          !$('.js-agree-to-gdpr').isOnScreen() &&
          Pollfish.helper.flag.showScrollDownMsg
        ) {
          Pollfish.helper.flag.showScrollDownMsg = false;
          Pollfish.ui.prompt(Pollfish.ui.getText('scroll_down'));
        }
      }
    },

    noSurveyAvailable() {
      Pollfish.helper.noSurveyAvailable = true;

      const { lastPayload } = Pollfish.store;
      const reward = Pollfish.helper.getRewardFromParticipation(lastPayload);

      if (
        Pollfish.helper.isDistributionLink() &&
        !Pollfish.helper.json.containsSurvey
      ) {
        Pollfish.ui.thankYouBasic();
        return;
      }

      if (lastPayload && lastPayload.status === 'success') {
        Pollfish.helper.sendSessionFinishedToServer();
        // being here mean that respondent has already completed previously a survey
        // after completion entered router but there has not additional survey
        // so we should show the thank you page. Also thank you page must know if
        // respondent sees the page after a router somehow to show the correct literals.
        Pollfish.ui.thankYouBasic({
          isResultPending: false,
          reward,
          payload: lastPayload,
          payloadIsFromLastPayload: true,
        });
        return;
      }
      if (!isEmpty(reward)) {
        Pollfish.ui.showScreenOut({ reward, payload: lastPayload });
      } else {
        // this can happen when we redirect from a third party with an empty content
        // and a lastSurveyId in the URL lastSurveyId gets replaced with null and sets
        // its value in the s_id property. see helper.js LOC 504
        const shouldCallNotEligible =
          Pollfish.helper.json.s_id || Pollfish.helper.json.containsSurvey;

        if (shouldCallNotEligible) {
          Pollfish.mobile.interface.userNotEligible();
        }

        // legacy code TBD in the future
        // this helps to close indicator permanently
        // since closeAndNoShow simply checks if the survey completed in SDK
        if (Pollfish.helper.json.sdk_version <= 5) {
          Pollfish.mobile.interface.setSurveyCompleted();
        }
      }

      Pollfish.helper.sendSessionFinishedToServer();
      const surveyId = get(lastPayload, 'surveyId');
      // The no survey available screen is defered because, in the case of an error
      // the function might get called together with some other rendering function.
      // Defering helps with keeping this function ontop (unless we also have defered
      // the conflicting render function, in which case... well...)
      setTimeout(() => {
        Pollfish.ui.thankYouBasic({
          payload: lastPayload,
          surveyId,
        });
      }, 0);
    },

    hideCloseButtonFromHeader() {
      const elements = document.getElementsByClassName(
        'header__action-close-btn',
      );
      if (elements && elements.length > 0) {
        const element = elements[0];
        element.style.display = 'none';
      }
    },

    hideCloseButtonFromHomescreen() {
      Pollfish.helper.closeButtonHidden = true;
      const elements = document.getElementsByClassName(
        'header__action-close-btn',
      );
      if (elements && elements.length > 0) {
        const element = elements[0];
        element.style.display = 'none';
      }
    },

    showCloseButtonAtHeader() {
      Pollfish.helper.closeButtonHidden = false;
      const elements = document.getElementsByClassName(
        'header__action-close-btn',
      );
      if (elements && elements.length > 0) {
        const element = elements[0];
        element.style.display = 'block';
      }
    },

    hideLogo() {
      const logoElement = document.querySelector('.header__logo');
      if (logoElement) {
        logoElement.style.display = 'none';
      }
      const headerElement = document.querySelector(
        '.header .header__container',
      );
      if (headerElement) {
        headerElement.style.height = 'unset';
      }
    },

    unhideLogo() {
      const logoElement = document.querySelector('.header__logo');
      if (logoElement) {
        logoElement.style.display = 'flex';
      }
      const headerElement = document.querySelector(
        '.header .header__container',
      );
      if (headerElement) {
        // the value that $header-height scss variable has
        headerElement.style.height = '67px';
      }
    },

    // download images for questions
    downloadImages(questionIndex) {
      const question = Pollfish.helper.json.questions[questionIndex];
      const questionImageToShow =
        question.optimized_image_url || question.image_url;
      const hasImageToShow = Boolean(questionImageToShow);
      // download question image if it exists
      if (hasImageToShow) {
        const questionImage = new Image();
        questionImage.src = questionImageToShow;
      }

      // download answer images if they exist
      for (let i = question.answers.length - 1; i >= 0; i -= 1) {
        const answer = question.answers[i];
        const answerDefaultImage =
          answer.optimized_image_url || answer.image_url;
        if (answerDefaultImage) {
          const answerImage = new Image();
          answerImage.src = answerDefaultImage;
        }
      }
      // download question poster if video exists
      if (question.video) {
        const questionPoster = new Image();
        questionPoster.src = question.video.poster;
      }

      const hasCustomTheme = Pollfish.helper.hasCustomSurveyTheme();
      const whiteLabelEnabled = Pollfish.helper.whiteLabelEnabled();
      // download images only once the first question has been answered
      if (
        questionIndex > 0 &&
        !Pollfish.helper.hasDownloadImages &&
        !hasCustomTheme &&
        !whiteLabelEnabled
      ) {
        Pollfish.ui.downloadPollfishImages();
      }
    },

    downloadPollfishImages() {
      const orcaImage = new Image();
      orcaImage.src = `${publicAssetsPrefix}/img/orca.png`;

      const searchImage = new Image();
      searchImage.src = `${publicAssetsPrefix}/img/search.png`;

      const thankYouImage = new Image();
      thankYouImage.src = `${publicAssetsPrefix}/img/thankyou.png`;

      const qualityScreenOutImage = new Image();
      qualityScreenOutImage.src = `${publicAssetsPrefix}/img/quality.png`;

      const feedbackImage = new Image();
      feedbackImage.src = `${publicAssetsPrefix}/img/feedback.jpg`;

      const cowImage = new Image();
      cowImage.src = `${publicAssetsPrefix}/img/cow.png`;

      const expiredImage = new Image();
      expiredImage.src = `${publicAssetsPrefix}/img/expired.png`;

      const hybridImage = new Image();
      hybridImage.src = `${publicAssetsPrefix}/img/hybrid.png`;

      const surveyAlreadyTakenImage = new Image();
      surveyAlreadyTakenImage.src = `${publicAssetsPrefix}/img/alreadytaken.png`;

      const notActive = new Image();
      notActive.src = `${publicAssetsPrefix}/img/notactive.png`;

      const historyEmptyOffers = new Image();
      historyEmptyOffers.src = `${publicAssetsPrefix}/img/no-offers.png`;

      Pollfish.helper.hasDownloadImages = true;
    },

    getQuestionWrapperElement(question) {
      const questionWrapper = document.querySelector(
        `.js-question[data-ref-id="${question.refId}"]`,
      );

      if (questionWrapper) {
        return questionWrapper;
      }
      return document.querySelector(`.js-question[id="${question.id}"]`);
    },

    getClosestQuestionWrapperElement(element) {
      return $(element).closest('.js-question');
    },

    isQuestionDisplaySamePage(element) {
      return $(element).attr('data-question-display-same-page') === 'true';
    },

    isSamePageValidatedOnce(element) {
      return $(element).attr('data-same-page-validated-once') === 'true';
    },

    setSamePageValidatedOnce(element, value = true) {
      $(element).attr('data-same-page-validated-once', !!value);
    },

    setSamePageIsValidAnswers(element, value = true) {
      $(element).attr('data-same-page-is-valid-answers', !!value);
    },

    showAnsweredQuestionsInSubmitButton() {
      const questions = $('.js-question');
      const questionsAnswered = $('*[data-same-page-is-valid-answers="true"]');

      if (questionsAnswered.length === questions.length) {
        Pollfish.ui.showCheckMarkInSubmitButton();
        return;
      }

      $('#submit-btn').addClass('btn-text-icon');
      $('#submit-btn').html(
        `<span class="flex flex-align-items--center">
          ${Pollfish.ui.getText(
            'warning.displaySamePage.submitButton',
            `${questionsAnswered.length}/${questions.length}`,
          )}
          <span class="icon-arrow-right title--md font-bold margin-xxs-left"></span>
        </span>`,
      );
    },

    showCheckMarkInSubmitButton() {
      $('#submit-btn').removeClass('btn-text-icon');
      $('#submit-btn').html('<span class="icon-check"></span>');
    },

    stickySubmitButton() {
      $('.main-content__holder').css({
        'padding-bottom': $('.submit-btn-container').outerHeight() + 124,
      });

      let classes = 'submit-btn-container-fixed';
      if (Pollfish.helper.hasIOSNavigator()) {
        classes += ' submit-btn-container-fixed-extra-padding';
      }
      $('.submit-btn-container').addClass(classes);
      Pollfish.ui.showAnsweredQuestionsInSubmitButton();
    },

    removeStickySubmitButton() {
      $('.main-content__holder').css({
        'padding-bottom': 0,
      });

      $('.submit-btn-container').removeClass(
        'submit-btn-container-fixed submit-btn-container-fixed-extra-padding',
      );
      Pollfish.ui.showCheckMarkInSubmitButton();
    },
  };
})();

export default Pollfish.ui;
